import { useEffect, useMemo, useState } from 'react';
import { App } from 'antd';
import { useNavigate } from 'react-router';
import WorkSpaceService from 'services/master/workspace';

const services = new WorkSpaceService();

const useComponent = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [packages, setPackages] = useState([]);
  const [packageFilter, setPackageFilter] = useState('all');
  const [expiryFilter, setExpiryFilter] = useState('all');
  const [renewTarget, setRenewTarget] = useState(null);
  const [renewPackageKey, setRenewPackageKey] = useState('mini');
  const [renewDuration, setRenewDuration] = useState(1);
  const [renewSubmitting, setRenewSubmitting] = useState(false);
  const [createDemoOpen, setCreateDemoOpen] = useState(false);
  const [createDemoShopName, setCreateDemoShopName] = useState('');
  const [createDemoSubmitting, setCreateDemoSubmitting] = useState(false);
  const [createDemoResult, setCreateDemoResult] = useState(null);

  const load = async () => {
    const [list, pkgList] = await Promise.all([
      services.list(),
      services.packageList(),
    ]);

    const sorted = Array.isArray(list)
      ? [...list].sort((a, b) => new Date(b?.create_on || 0) - new Date(a?.create_on || 0))
      : [];

    setData(sorted);
    setPackages(Array.isArray(pkgList) ? pkgList : []);
  };

  useEffect(() => {
    load();
  }, []);

  const isInvalidateShop = (record) => {
    const tier = record?.subscription?.tier || 'mien_phi';
    const expiryOn = record?.subscription?.expiry_on;
    if (tier !== 'mien_phi' || !expiryOn) {
      return false;
    }

    const expiryTime = new Date(expiryOn).getTime();
    if (Number.isNaN(expiryTime)) {
      return false;
    }

    return expiryTime < Date.now();
  };

  const filteredData = useMemo(() => {
    const normalizedKeyword = String(keyword || '').trim().toLowerCase();
    const now = new Date();

    let list = data;

    if (normalizedKeyword) {
      list = list.filter((record) => {
        const values = [
          record?.web_site?.title,
          record?.web_site?.url,
          record?.owner?.full_name,
          record?.owner?.phone,
        ]
          .filter(Boolean)
          .map((value) => String(value).toLowerCase());

        return values.some((value) => value.includes(normalizedKeyword));
      });
    }

    if (expiryFilter === 'expiring_7d') {
      list = list.filter((record) => {
        const expiryOn = record?.subscription?.expiry_on;
        if (!expiryOn) return false;
        const diffMs = new Date(expiryOn).getTime() - now.getTime();
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        return diffDays >= 0 && diffDays <= 7;
      });
    }

    if (packageFilter !== 'all') {
      list = list.filter((record) => {
        const tier = record?.subscription?.tier || 'mien_phi';
        if (packageFilter === 'mien_phi') {
          return tier === 'mien_phi' && !isInvalidateShop(record);
        }
        return tier === packageFilter;
      });
    }

    return list;
  }, [data, keyword, expiryFilter, packageFilter]);

  const packageMap = useMemo(() => {
    return (packages || []).reduce((acc, item) => {
      acc[item?.key] = item;
      return acc;
    }, {});
  }, [packages]);

  const getPackageName = (record) => {
    if (isInvalidateShop(record)) {
      return 'invalidate';
    }
    const tier = record?.subscription?.tier || 'mien_phi';
    return packageMap[tier]?.name || tier;
  };

  const openRenewModal = (record) => {
    setRenewTarget(record);
    setRenewPackageKey(record?.subscription?.tier || 'mini');
    setRenewDuration(1);
  };

  const closeRenewModal = () => {
    if (renewSubmitting) return;
    setRenewTarget(null);
  };

  const submitRenew = async () => {
    if (!renewTarget?.id) return;
    setRenewSubmitting(true);
    try {
      await services.renew({
        w_id: renewTarget.id,
        package_key: renewPackageKey,
        duration_months: renewDuration,
      });
      message.success('Gia hạn gói cước thành công');
      setRenewTarget(null);
      await load();
    } catch (error) {
      message.error(error?.message || 'Gia hạn thất bại');
    } finally {
      setRenewSubmitting(false);
    }
  };

  const onExpiryFilterChange = (value) => {
    setExpiryFilter(value || 'all');
  };

  const onPackageFilterChange = (value) => {
    setPackageFilter(value || 'all');
  };

  const onKeywordChange = (event) => {
    setKeyword(event?.target?.value || '');
  };

  const openCreateDemoModal = () => {
    setCreateDemoOpen(true);
  };

  const closeCreateDemoModal = () => {
    if (createDemoSubmitting) return;
    setCreateDemoOpen(false);
  };

  const closeCreateDemoResult = () => {
    setCreateDemoResult(null);
  };

  const onCreateDemoShopNameChange = (event) => {
    setCreateDemoShopName(event?.target?.value || '');
  };

  const submitCreateDemo = async () => {
    const shop_name = String(createDemoShopName || '').trim();
    if (!shop_name) {
      message.error('Vui lòng nhập tên shop');
      return;
    }

    setCreateDemoSubmitting(true);
    try {
      const result = await services.createDemo({ shop_name });
      setCreateDemoOpen(false);
      setCreateDemoShopName('');
      setCreateDemoResult(result || null);
      message.success('Tạo shop demo thành công');
      await load();
    } catch (error) {
      message.error(error?.message || 'Tạo shop demo thất bại');
    } finally {
      setCreateDemoSubmitting(false);
    }
  };

  const copyText = async (text, label = 'Nội dung') => {
    try {
      await navigator.clipboard.writeText(String(text || ''));
      message.success(`Đã copy ${label}`);
    } catch (error) {
      message.error('Copy thất bại');
    }
  };

  const copyAllCredentials = async () => {
    if (!createDemoResult) return;
    const lines = [
      `Shop URL: https://${createDemoResult?.shop_url}.sipro.store`,
      `Username: ${createDemoResult?.username || ''}`,
      `Login ID: ${createDemoResult?.login_id || ''}`,
      `Password: ${createDemoResult?.password || ''}`,
      `Workspace ID: ${createDemoResult?.w_id || ''}`,
    ];
    await copyText(lines.join('\n'), 'thông tin đăng nhập');
  };

  const goToDetail = (w_id) => navigate(`/workspace/${w_id}`);

  return {
    data,
    filteredData,
    keyword,
    packages,
    packageFilter,
    expiryFilter,
    renewTarget,
    renewPackageKey,
    renewDuration,
    renewSubmitting,
    getPackageName,
    openRenewModal,
    closeRenewModal,
    submitRenew,
    setRenewPackageKey,
    setRenewDuration,
    createDemoOpen,
    createDemoShopName,
    createDemoSubmitting,
    createDemoResult,
    onKeywordChange,
    onPackageFilterChange,
    onExpiryFilterChange,
    openCreateDemoModal,
    closeCreateDemoModal,
    onCreateDemoShopNameChange,
    submitCreateDemo,
    closeCreateDemoResult,
    copyText,
    copyAllCredentials,
    goToDetail,
  };
};

export default useComponent;